/* eslint-disable arrow-body-style */

import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { PROD_REFERRAL_WEBFORM_URL, STAGE_REFERRAL_WEBFORM_URL } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useEnvironment } from "@src/context/EnvironmentContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useThemeContext } from "@src/context/ThemeContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import ClientLogsService from "@src/services/client-logs.service";
import type { Partner } from "@src/services/partners.service";
import { fetchPartners } from "@src/services/partners.service";
import { upsertReferrer } from "@src/services/referrer.service";
import { fetchReferrerTeams, type ReferrerTeam } from "@src/services/referrer-teams.service";
import { getThemeNameFromTag } from "@src/themes";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import logger from "@src/utils/logger";
import { useLocation, useParams } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Fragment, type SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { handleReferScheduleNowSuccess } from "./refer-success-navigation";
import { markReferThemeChangeInProgress } from "./themeChangeSignal";

const PartnerRow = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  gap: "24px",
  variants: {
    isMobile: {
      true: { flexDirection: "column", gap: "16px" },
      false: { flexDirection: "row" },
    },
  },
});

const PartnerInfoArea = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "16px",
  flex: 1,
  minWidth: 0,
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
        gap: "12px",
      },
    },
  },
});

const PartnerIconCircle = styled("div", {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  backgroundColor: "$app.color.top.background",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const PartnerTextContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const PartnerLabel = styled("div", {
  fontSize: "16px",
  fontWeight: 700,
  color: "$app.color.text.primary",
});

const PartnerDescription = styled("div", {
  fontSize: "14px",
  color: "$app.color.text.primary",
  lineHeight: "20px",
  opacity: 0.8,
});

const PartnerInputArea = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  variants: {
    isMobile: {
      true: { width: "100%" },
      false: { flex: 1, minWidth: "240px" },
    },
  },
});

const initialFormState = (data: any) => ({
  partners: [] as Partner[],
  referrerTeams: [] as any[],
  selectedPartner: data.selectedPartner || "",
  selectedReferrerTeam: data.selectedReferrerTeam || "",
});

interface ReferralWebformUrlInput {
  appEnvironment: string;
  currentSearch: string;
  partners: Partner[];
  selectedPartnerId: string;
  referrerTeams: ReferrerTeam[];
  selectedReferrerTeamId: string;
  unknownReferrerTeamId: string;
}

export const buildReferralWebformUrl = ({
  appEnvironment,
  currentSearch,
  partners,
  selectedPartnerId,
  referrerTeams,
  selectedReferrerTeamId,
  unknownReferrerTeamId,
}: ReferralWebformUrlInput): string => {
  const url = appEnvironment === "prod" ? PROD_REFERRAL_WEBFORM_URL : STAGE_REFERRAL_WEBFORM_URL;
  const queryParams = new URLSearchParams(currentSearch);
  const effectivePartnerId =
    selectedPartnerId || partners.find((partner) => partner.name === "Referral Webform")?.id || "";

  if (effectivePartnerId) {
    const selectedPartner = partners.find((partner) => partner.id === effectivePartnerId);
    if (selectedPartner) {
      queryParams.append("partnerId", selectedPartner.id);
      queryParams.append("partnerName", selectedPartner.name);
    }
  }

  const effectiveReferrerTeamId = selectedReferrerTeamId || unknownReferrerTeamId;
  if (effectiveReferrerTeamId) {
    const selectedReferrerTeam = referrerTeams.find((team) => team.id === effectiveReferrerTeamId);
    const referrerTeamName = selectedReferrerTeam?.name ?? "General Referral Team";
    if (referrerTeamName !== "General Referral Team") {
      queryParams.append("referrerTeamId", effectiveReferrerTeamId);
    }
    queryParams.append("referrerTeamName", referrerTeamName);
  }

  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

type ReferFormState = ReturnType<typeof initialFormState>;

const getSelectedPartnerValue = (
  previousState: ReferFormState,
  singlePartnerId: string,
  shouldAutoSelectPartner: boolean,
  shouldClearSelection: boolean,
) => {
  if (shouldAutoSelectPartner) {
    return singlePartnerId;
  }

  if (shouldClearSelection) {
    return "";
  }

  return previousState.selectedPartner;
};

const getUpdatedFormStateForPartners = (
  previousState: ReferFormState,
  partners: Partner[],
): ReferFormState => {
  const singlePartnerId = partners.length === 1 ? partners[0].id : "";
  const shouldAutoSelectPartner = singlePartnerId !== "";
  const hasSelectedPartnerChanged =
    shouldAutoSelectPartner && previousState.selectedPartner !== singlePartnerId;

  const isPreviousPartnerStillValid =
    previousState.selectedPartner !== "" &&
    partners.some((partner) => partner.id === previousState.selectedPartner);

  const shouldClearSelection = !shouldAutoSelectPartner && !isPreviousPartnerStillValid;
  const shouldResetReferrerState = hasSelectedPartnerChanged || shouldClearSelection;

  return {
    ...previousState,
    partners,
    selectedPartner: getSelectedPartnerValue(
      previousState,
      singlePartnerId,
      shouldAutoSelectPartner,
      shouldClearSelection,
    ),
    referrerTeams: shouldResetReferrerState ? [] : previousState.referrerTeams,
    selectedReferrerTeam: shouldResetReferrerState ? "" : previousState.selectedReferrerTeam,
  };
};

interface ReferFormProps {
  isMobile: boolean;
}

export const ReferForm = ({ isMobile }: ReferFormProps) => {
  const { data, markRouteAsComplete, setData } = useApplicationContext();
  const { APP_ENV } = useEnvironment();
  const { activeThemeName, setBranding } = useThemeContext();
  const { parentOrganization: pathParentOrg } = useParams();
  const location = useLocation();
  const parentOrganization =
    pathParentOrg || new URLSearchParams(location.search).get("parentOrganization") || undefined;
  const [formState, setFormState] = useState(initialFormState(data));
  const [errors, setErrors] = useState({
    selectedPartner: "",
    selectedReferrerTeam: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { runExclusive } = useInteractionGuard();
  const [referrerTeamsLoadedForPartner, setReferrerTeamsLoadedForPartner] = useState("");
  const [unknownReferrerTeamId, setUnknownReferrerTeamId] = useState("");
  const selectedPartnerRef = useRef(formState.selectedPartner);
  const navigate = useNavigateWithUTM();

  useEffect(() => {
    selectedPartnerRef.current = formState.selectedPartner;
  }, [formState.selectedPartner]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    const loadPartners = async () => {
      if (formState.partners.length > 0 || errors.selectedPartner !== "" || isRedirecting) return;

      setIsLoading(true);
      const partners = await fetchPartners(parentOrganization, data.userUuid);

      if (!Array.isArray(partners)) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedPartner: "Failed to load partners. Please try refreshing the page.",
        }));

        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ReferForm.loadPartners",
          "Failed to load partners.",
          new Error(partners.error),
        );

        setIsLoading(false);

        return;
      }

      setFormState((previousState) => getUpdatedFormStateForPartners(previousState, partners));

      if (partners.length === 1) {
        setReferrerTeamsLoadedForPartner("");
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedPartner: "",
          selectedReferrerTeam: "",
        }));
      }

      setIsLoading(false);
    };

    loadPartners();
  }, [isRedirecting, parentOrganization]);

  useEffect(() => {
    const loadReferrerTeams = async () => {
      if (
        formState.selectedPartner === "" ||
        referrerTeamsLoadedForPartner === formState.selectedPartner ||
        errors.selectedReferrerTeam !== "" ||
        isRedirecting
      )
        return;

      setIsLoading(true);
      const selectedPartner = formState.selectedPartner;
      const referrerTeams = await fetchReferrerTeams(selectedPartner, data.userUuid || undefined);

      if (selectedPartnerRef.current !== selectedPartner) {
        setIsLoading(false);

        return;
      }

      if (referrerTeams.error) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedReferrerTeam: "Failed to load referrer teams. Please try refreshing the page.",
        }));

        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ReferForm.loadReferrerTeams",
          "Failed to load referrer teams.",
          new Error(referrerTeams.error),
        );

        setIsLoading(false);

        return;
      }

      setFormState((previousState) => {
        if (previousState.selectedPartner !== selectedPartner) {
          return previousState;
        }

        const singleReferrerTeamId = referrerTeams.length === 1 ? referrerTeams[0].id : "";
        const shouldAutoSelectReferrerTeam = singleReferrerTeamId !== "";

        return {
          ...previousState,
          referrerTeams,
          selectedReferrerTeam: shouldAutoSelectReferrerTeam
            ? singleReferrerTeamId
            : previousState.selectedReferrerTeam,
        };
      });
      setReferrerTeamsLoadedForPartner(selectedPartner);

      if (referrerTeams.length === 1) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          selectedReferrerTeam: "",
        }));
      }

      setIsLoading(false);
    };

    loadReferrerTeams();
  }, [
    formState.selectedPartner,
    referrerTeamsLoadedForPartner,
    errors.selectedReferrerTeam,
    isRedirecting,
  ]);

  // Fetch Unknown partner's referrer teams eagerly so we have the Unknown referrer team ID
  // available as a fallback when user doesn't select a referrer team
  useEffect(() => {
    let isCancelled = false;

    const loadUnknownReferrerTeamId = async () => {
      const unknownPartnerId = formState.partners.find((p) => p.name === "Referral Webform")?.id;
      if (!unknownPartnerId) return;

      const referrerTeams = await fetchReferrerTeams(unknownPartnerId, data.userUuid || undefined);

      if (isCancelled) return;
      if (!Array.isArray(referrerTeams)) return;

      const unknownTeamId = referrerTeams.find((t) => t.name === "General Referral Team")?.id ?? "";
      setUnknownReferrerTeamId(unknownTeamId);
    };

    loadUnknownReferrerTeamId();
    return () => {
      isCancelled = true;
    };
  }, [formState.partners, data.userUuid]);

  // Enable the idle timer as soon as the user has made their first selection,
  // regardless of whether the downstream fetch succeeded.
  useEffect(() => {
    if (formState.selectedPartner && !data.hasReferFormInput) {
      setData("hasReferFormInput", true);
    }
  }, [data.hasReferFormInput, formState.selectedPartner, setData]);

  useEffect(() => {
    setData("isReferFormLoading", isLoading);
  }, [isLoading, setData]);

  useEffect(() => {
    setData("selectedPartner", formState.selectedPartner);
    setData("selectedReferrerTeam", formState.selectedReferrerTeam);
  }, [formState.selectedPartner, formState.selectedReferrerTeam, setData]);

  useEffect(() => {
    if (formState.partners.length === 0) {
      return;
    }

    const selectedPartner = formState.partners.find(
      (partner) => partner.id === formState.selectedPartner,
    );

    if (getThemeNameFromTag(selectedPartner?.branding) !== activeThemeName) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "ReferForm.useEffect[activeThemeName, formState.partners, formState.selectedPartner]",
        `Partner ${selectedPartner?.name} has different branding than active theme. Triggering theme change.`,
      );
      markReferThemeChangeInProgress();
    }

    setBranding(selectedPartner?.branding);
  }, [activeThemeName, formState.partners, formState.selectedPartner, setBranding]);

  const ReferFormWrapper = useMemo(
    () =>
      styled("form", {
        width: "100%",
        maxWidth: "860px",
      }),
    [],
  );

  const validateForm = () => {
    const newErrors = { selectedPartner: "", selectedReferrerTeam: "" };

    // Referrer team is optional - user can proceed without selecting one

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === "");
  };

  const handleChange = (field: keyof typeof formState, newFieldValue: string) => {
    if (field === "selectedPartner") {
      selectedPartnerRef.current = newFieldValue;
      setReferrerTeamsLoadedForPartner("");
      setFormState((previousState) => ({
        ...previousState,
        [field]: newFieldValue,
        referrerTeams: [],
        selectedReferrerTeam: "",
      }));
    } else {
      setFormState((previousState) => ({
        ...previousState,
        [field]: newFieldValue,
      }));
    }
    setErrors({ selectedPartner: "", selectedReferrerTeam: "" });
  };

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    const isFormValid = validateForm();

    if (isFormValid) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "ReferForm.handleSubmit",
        "Form validation passed, submitting form.",
        undefined,
        {
          text: `Refer form submitted. Partner: ${formState.selectedPartner}. Referrer team: ${formState.selectedReferrerTeam}.`,
        },
      );
      if (
        ((event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement)?.name === "scheduleNow"
      ) {
        await runExclusive("refer:schedule-now", async () => {
          const response = await submitReferrer({ requestedCallback: false });

          if (response.error) {
            ClientLogsService.logError(
              data.userUuid || "unknown",
              "ReferForm.handleSubmit",
              "Failed to submit referrer information for 'Schedule Now' scenario - Redirecting to Contact Us",
              new Error(response.error),
            );
            logger.error({ error: response.error });
            setIsLoading(false);
            navigateToContactUs(navigate, data.userUuid, undefined, false);
          } else {
            ClientLogsService.logWebAction(
              data.userUuid || "unknown",
              "ReferForm.handleSubmit",
              "Successfully submitted referrer information for 'Schedule Now' scenario - Redirecting Get Started.",
            );
            handleReferScheduleNowSuccess(
              formState.partners,
              formState.selectedPartner,
              setData,
              markRouteAsComplete,
              setIsLoading,
              navigate,
              location,
            );
          }
        });
      } else {
        await runExclusive("refer:request-callback", async () => {
          await submitReferrer({ requestedCallback: true });
          setIsRedirecting(true);
          ClientLogsService.logWebAction(
            data.userUuid || "unknown",
            "ReferForm.handleSubmit",
            "Submitted referrer information for 'Request a callback' scenario, redirecting to referral webform.",
          );
        });
        globalThis.location.href = referralWebformUrl();
      }
    } else {
      ClientLogsService.logUserError(
        data.userUuid || "unknown",
        "ReferForm.handleSubmit",
        "Form has validation errors, unable to proceed",
      );
    }

    setIsLoading(false);
  };

  const submitReferrer = async ({ requestedCallback }: { requestedCallback: boolean }) => {
    const partnerId =
      formState.selectedPartner ||
      formState.partners.find((p) => p.name === "Referral Webform")?.id ||
      undefined;
    const referrerTeamId = formState.selectedReferrerTeam || unknownReferrerTeamId || undefined;

    return await upsertReferrer({
      user_uuid: data.userUuid,
      role: "referrer",
      partner_id: partnerId,
      referrer_team_id: referrerTeamId,
      requested_callback: requestedCallback,
    });
  };

  const referralWebformUrl = () => {
    return buildReferralWebformUrl({
      appEnvironment: APP_ENV,
      currentSearch: globalThis.location.search,
      partners: formState.partners,
      selectedPartnerId: formState.selectedPartner,
      referrerTeams: formState.referrerTeams,
      selectedReferrerTeamId: formState.selectedReferrerTeam,
      unknownReferrerTeamId,
    });
  };

  const partnerOptions = useMemo(
    () =>
      formState.partners
        .filter((partner) => partner.name !== "Referral Webform")
        .map((partner) => ({
          value: partner.id,
          label: partner.name,
        })),
    [formState.partners],
  );

  const referrerTeamOptions = useMemo(
    () =>
      formState.referrerTeams
        .filter((referrerTeam) => referrerTeam.name !== "General Referral Team")
        .map((referrerTeam) => ({
          value: referrerTeam.id,
          label: referrerTeam.name,
        })),
    [formState.referrerTeams],
  );

  const isSinglePartnerOption = formState.partners.length === 1;
  const isSingleReferrerTeamOption = formState.referrerTeams.length === 1;
  const shouldShowReferrerTeamInput =
    formState.selectedPartner !== "" &&
    (isRedirecting ||
      referrerTeamsLoadedForPartner === formState.selectedPartner ||
      errors.selectedReferrerTeam !== "");

  return (
    <Fragment>
      <LoadingOverlay isLoading={isLoading} />
      <ReferFormWrapper id="refer-form" onSubmit={(event) => handleSubmit(event)}>
        <Card className={isMobile ? "v2card-form-mobile-style" : ""} css={{ padding: "24px" }}>
          <PartnerRow isMobile={isMobile}>
            <PartnerInfoArea isMobile={isMobile}>
              <PartnerIconCircle>
                <IconSymbol icon="group" variant="outlined" size={28} />
              </PartnerIconCircle>
              <PartnerTextContent>
                <PartnerLabel>Partner (optional)</PartnerLabel>
                <PartnerDescription>
                  If your organization is listed, please type/select it here. You can continue
                  without selecting a partner.
                </PartnerDescription>
              </PartnerTextContent>
            </PartnerInfoArea>
            <PartnerInputArea isMobile={isMobile}>
              <SelectInput
                label="Select partner"
                value={formState.selectedPartner || undefined}
                onChange={(value) => handleChange("selectedPartner", value as string)}
                isRequired={false}
                isSearchable={true}
                isDisabled={isSinglePartnerOption}
                options={partnerOptions}
                errorMessage={errors.selectedPartner}
                data-testid="test-id-refer-partner-select"
              />
            </PartnerInputArea>
          </PartnerRow>
          {shouldShowReferrerTeamInput && (
            <PartnerRow isMobile={isMobile} css={{ marginTop: "24px" }}>
              <PartnerInfoArea isMobile={isMobile}>
                <PartnerIconCircle>
                  <IconSymbol icon="groups" variant="outlined" size={28} />
                </PartnerIconCircle>
                <PartnerTextContent>
                  <PartnerLabel>Referrer Team (optional)</PartnerLabel>
                  <PartnerDescription>
                    If you know your referrer team, select it. You can continue without selecting a
                    referrer team.
                  </PartnerDescription>
                </PartnerTextContent>
              </PartnerInfoArea>
              <PartnerInputArea isMobile={isMobile}>
                <SelectInput
                  label="Select referrer team"
                  value={formState.selectedReferrerTeam || undefined}
                  onChange={(value) => handleChange("selectedReferrerTeam", value as string)}
                  isRequired={false}
                  isSearchable={true}
                  isDisabled={isSingleReferrerTeamOption}
                  options={referrerTeamOptions}
                  errorMessage={errors.selectedReferrerTeam}
                  data-testid="test-id-refer-referrer-team-select"
                />
              </PartnerInputArea>
            </PartnerRow>
          )}
        </Card>
      </ReferFormWrapper>
    </Fragment>
  );
};
